import React, { useMemo } from 'react'
import { FormAutosuggest, useZionFormContext } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import { PersonGroup, ContentEdit } from '@fs/zion-icon'
import { List, ListItem } from '@fs/zion-ui'
import { useFeatureFlag } from '@fs/zion-flags'
import { useSlsStateContext } from '@fs/zion-sls'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import RowWrapper from '../uiHelpers/RowWrapper'
import FieldWrapper from '../uiHelpers/FieldWrapper'
import HighlightButtonOld from '../shared/HighlightButtonOld'
import { getEventTypeSuggestions, EVENT_FIELD_TYPES as eventFieldTypes } from './utils/eventUtils'
import RelationshipInputs from './RelationshipInputs'
import EventFieldsCard from './EventFieldsCard'
import { autosuggestPrioritySort } from '../../../../../shared/utils/utils'

const EventFormFields = ({ person, personName, relationshipOptions, showRelationshipEvents, useSharedEvent }) => {
  const { types } = useSlsStateContext()
  const { openOverlay } = useOverlayContext()
  const { getValues, watch, setValue } = useZionFormContext()
  const [t] = useTranslation()
  const sharedEventPanelFlag = useFeatureFlag('rip_sharedImageViewer_sharedEventPanel')
  const [id, eventTypeField, isPrimary, newEvent] = getValues(['id', 'eventTypeField', 'primary', 'newEvent'])
  const eventType = watch('eventType')
  const selectData = useMemo(
    () => getEventTypeSuggestions({ t, types, isPrimary, showRelationshipEvents }).reverse(),
    [types, t, isPrimary, showRelationshipEvents]
  )
  const isRelationshipEvent = useMemo(() => eventType?.elementTypes?.includes('RELATIONSHIP'), [eventType])
  const shouldDisableEventSelector = useMemo(() => !newEvent && isRelationshipEvent, [newEvent, isRelationshipEvent])

  const fieldSections = useMemo(
    () => [
      {
        fieldType: eventFieldTypes.DATE,
        heading: t('edit-events.fields.date-heading', 'Date'),
        fieldSelectorHeading: t('edit-events.fields.date-overlay-title', 'Add Date Fields'),
      },
      {
        fieldType: eventFieldTypes.PLACE,
        heading: t('edit-events.fields.place-heading', 'Place'),
        fieldSelectorHeading: t('edit-events.fields.place-overlay-title', 'Add Place Fields'),
      },
      {
        fieldType: eventFieldTypes.EVENT,
        heading: t('edit-events.fields.others-heading', 'Others'),
        fieldSelectorHeading: t('edit-events.fields.other-overlay-title', 'Add Other Fields'),
      },
    ],
    [t]
  )

  return (
    <>
      <RowWrapper>
        <FieldWrapper alignY="bottom" indent={0} size="100%" maxWidth="325px" growX>
          <FormAutosuggest
            name="eventType"
            label={t('edit-events.fields.event-type', 'Event Type')}
            data-testid="event-type-autosuggest"
            placeholder={t('edit-events.fields.event-type', 'Event Type')}
            suggestions={selectData}
            onSuggestionSelected={(val) => {
              setValue('eventTypeField', { ...eventTypeField, label: val?.primaryText }, { shouldDirty: true })
              setValue(id, val?.key, { shouldDirty: true })
            }}
            clearOnClickAway
            clearable
            virtualize
            disabled={shouldDisableEventSelector}
            sortFunction={autosuggestPrioritySort}
          />
          <HighlightButtonOld field={{ ...eventTypeField }} />
        </FieldWrapper>
      </RowWrapper>
      {!isRelationshipEvent && sharedEventPanelFlag.isOn && useSharedEvent && (
        <List>
          <ListItem
            startElement={<ListItem.Icon Icon={PersonGroup} />}
            endElement={
              <ListItem.IconButton
                title={t('edit-events.link-event', 'Link Event to Other Names')}
                type="shared event"
                Icon={ContentEdit}
                onClick={() => {
                  openOverlay('sharedEvent')
                }}
              />
            }
            primaryText={t('edit-events.link-event', 'Link Event to Other Names')}
            secondaryText={t('edit-events.link-event-description', 'Add or remove names linked to this event.')}
          />
        </List>
      )}
      <RowWrapper alignY="top">
        {isRelationshipEvent && (
          <RelationshipInputs
            person={person}
            personName={personName}
            eventLabel={eventType.primaryText}
            relationshipOptions={relationshipOptions}
            noGrowY
            growX
          />
        )}
        {fieldSections.map(({ fieldType, heading, fieldSelectorHeading }) => (
          <EventFieldsCard
            key={fieldType}
            fieldType={fieldType}
            heading={heading}
            fieldSelectorHeading={fieldSelectorHeading}
            noGrowY
            growX
          />
        ))}
      </RowWrapper>
    </>
  )
}

export default EventFormFields
